import React, { useState } from "react";
import CrudPage from "@/components/CrudPage";
import { StatusBadge, useLookup, useSuppliers } from "@/components/shared";
import AssetQrDialog from "@/components/AssetQrDialog";
import QrPosterSheet from "@/components/QrPosterSheet";
import { useAuth } from "@/context/AuthContext";
import AssetQrInline from "@/components/AssetQrInline";
import AssetTelemetryTab from "@/components/AssetTelemetryTab";
import Vehicles from "@/pages/Vehicles";
import { QrCode, Wrench, Car, Printer } from "@phosphor-icons/react";

const STATUS_OPTS = ["available", "on_hire", "off_hire", "stand_down", "maintenance"];

function PlantEquipment() {
  const assetTypes = useLookup("asset_types");
  const suppliers = useSuppliers();
  const { isAdmin } = useAuth();
  const [qrAsset, setQrAsset] = useState(null);
  const [showPosters, setShowPosters] = useState(false);

  return (
    <>
      <CrudPage
        title="Plant & Equipment"
        singular="Plant & Equipment"
        subtitle="Equipment available for hire, grouped by supplier"
        endpoint="/assets"
        headerActions={isAdmin ? (
          <button onClick={() => setShowPosters(true)} data-testid="print-qr-posters-btn"
            className="h-9 px-3 border border-equipr-black text-equipr-black font-semibold text-sm rounded-none hover:bg-gray-100 inline-flex items-center gap-1.5">
            <Printer size={15} weight="bold" /> QR Posters
          </button>
        ) : null}
        dynamicOptions={{ asset_type: assetTypes, supplier: suppliers, status: STATUS_OPTS }}
        extraTabs={[
          { key: "qr", label: "QR / Docket", render: ({ form }) => <AssetQrInline asset={form} /> },
          { key: "telemetry", label: "Telemetry", render: ({ editing }) => <AssetTelemetryTab editing={editing} /> },
        ]}
        rowActions={(row) => (
          <button onClick={() => setQrAsset(row)} data-testid={`asset-qr-${row.id}`}
            title="Docket QR + PIN"
            className="text-gray-500 hover:text-equipr-black inline-flex items-center">
            <QrCode size={17} />
          </button>
        )}
        columns={[
          { key: "asset_no", label: "Asset #", render: (v, row) => (
            <span className="font-semibold">{v}{row.is_auxiliary && <span className="ml-1.5 inline-block bg-gray-200 text-gray-700 text-[10px] font-bold px-1 py-px align-middle" data-testid={`aux-badge-${v}`}>ATTACH.</span>}</span>
          ) },
          { key: "asset_type", label: "Type" },
          { key: "ext_asset_id", label: "Ext. ID" },
          { key: "supplier", label: "Supplier" },
          { key: "docket_pin", label: "Docket PIN", render: (v) => <span className="font-mono tracking-widest text-gray-600">{v || "—"}</span> },
          { key: "last_location", label: "Last Location", render: (v, row) => v ? (
            <span>{v}{row.last_location_at && <span className="block text-[10px] text-gray-400">{new Date(row.last_location_at).toLocaleDateString("en-AU", { day: "2-digit", month: "short" })}</span>}</span>
          ) : "—" },
          { key: "current_smu", label: "SMU", render: (v, row) => v != null ? (
            <span className="font-mono">{v}{row.current_smu_at && <span className="block text-[10px] text-gray-400">{new Date(row.current_smu_at).toLocaleDateString("en-AU", { day: "2-digit", month: "short" })}</span>}</span>
          ) : "—" },
          { key: "status", label: "Status", render: (v) => <StatusBadge value={v} /> },
        ]}
        fields={[
          { key: "asset_no", label: "Asset #", required: true },
          { key: "asset_type", label: "Asset Type", type: "select", required: true },
          { key: "cust_asset_type", label: "Cust. Asset Type" },
          { key: "ext_asset_id", label: "External Asset ID" },
          { key: "supplier", label: "Supplier", type: "select" },
          { key: "status", label: "Status", type: "select", default: "available" },
          { key: "docket_pin", label: "Docket PIN (auto if blank)" },
          { key: "last_location", label: "Last Location" },
          { key: "is_auxiliary", label: "Auxiliary / Attachment", type: "checkbox", checkboxLabel: "Attachment left on site (rock breaker, compaction wheel…)", default: false },
          { key: "service_due_date", label: "Service Due Date", type: "date" },
          { key: "service_due_smu", label: "Service Due (SMU)", type: "number" },
          { key: "linked_assets", label: "Linked Assets", type: "tags" },
          { key: "notes", label: "Notes", type: "textarea", full: true },
        ]}
      />
      {qrAsset && <AssetQrDialog asset={qrAsset} onClose={() => setQrAsset(null)} />}
      {showPosters && <QrPosterSheet onClose={() => setShowPosters(false)} />}
    </>
  );
}

export default function Assets() {
  const [tab, setTab] = useState("plant");
  return (
    <div className="h-full flex flex-col bg-surface-page">
      <div className="flex items-center gap-1 bg-surface-page px-4 pt-3 shrink-0">
        <div className="flex items-center bg-surface-inset rounded-[999px] p-0.5">
        <button onClick={() => setTab("plant")} data-testid="assets-tab-plant"
          className={`px-3.5 h-8 rounded-[999px] text-[12px] font-bold uppercase tracking-[0.04em] inline-flex items-center gap-1.5 ${tab === "plant" ? "bg-equipr-yellow text-black" : "text-ink-secondary hover:text-ink-primary"}`}>
          <Wrench size={14} weight="bold" /> Plant & Equipment
        </button>
        <button onClick={() => setTab("vehicles")} data-testid="assets-tab-vehicles"
          className={`px-3.5 h-8 rounded-[999px] text-[12px] font-bold uppercase tracking-[0.04em] inline-flex items-center gap-1.5 ${tab === "vehicles" ? "bg-equipr-yellow text-black" : "text-ink-secondary hover:text-ink-primary"}`}>
          <Car size={14} weight="bold" /> Vehicles
        </button>
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        {tab === "plant" ? <PlantEquipment /> : <Vehicles />}
      </div>
    </div>
  );
}
